import React, { useState, useEffect } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import axios from 'axios';
import { Box, Button, Checkbox, FormControlLabel, Typography, Paper, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Color } from 'core';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  reportCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${Color.ceregoGreen}`,
  },
  reportTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  reportDescription: {
    color: Color.reportGray,
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    marginTop: theme.spacing(3),
  },
  successMessage: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: Color.successGreen,
    borderRadius: 4,
  },
  errorMessage: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: Color.reportRed,
    borderRadius: 4,
  },
}));

interface ReportOption {
  id: string;
  type: string;
  title: string;
  description: string;
}

export const AdminEmailedReports: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const partner = user?.primaryPartner;
  const hasEmail = Boolean(user?.email);

  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [includeDepartments, setIncludeDepartments] = useState(false);
  const [hasDepartments, setHasDepartments] = useState(false);
  const [, setDepartmentsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<React.ReactNode | string>('');
  const [errorMessage, setErrorMessage] = useState('');

  // Check if partner has departments
  useEffect(() => {
    const checkForDepartments = async () => {
      if (!partner) {
        setDepartmentsLoading(false);
        return;
      }

      try {
        setDepartmentsLoading(true);
        const { data } = await axios.get(`/api/v3/partners/${partner.id}/departments`);
        // If there are any departments, set hasDepartments to true
        setHasDepartments(data && data.data && data.data.length > 0);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setHasDepartments(false);
      } finally {
        setDepartmentsLoading(false);
      }
    };

    checkForDepartments();
  }, [partner]);

  const reportOptions: ReportOption[] = [
    {
      id: 'courses',
      type: 'courses',
      title: t('Courses Report'),
      description: t(
        'CSV report containing Course ID, Title, Created At date, and number of Course Users (including removed users).'
      ),
    },
    {
      id: 'total_study_time',
      type: 'total_study_time',
      title: t('Total Study Time Report'),
      description: t('CSV report showing total study time in minutes aggregated by month.'),
    },
    {
      id: 'full_learner_stats',
      type: 'full_learner_stats',
      title: t('Full Learner Stats Report'),
      description: t(
        'Detailed CSV with complete user data including IDs, stats (Agility, Diligence, Knowledge), activity dates, course enrollment/completion, progress metrics, device usage, and LTI ID.'
      ),
    },
    {
      id: 'admins',
      type: 'admins',
      title: t('Partner Administrators Report'),
      description: t(
        'CSV report listing all administrators with their User ID, Partner ID, Partner Name, Role, User Name, Last Login date, and Email address.'
      ),
    },
    {
      id: 'groups',
      type: 'groups',
      title: t('Groups Report'),
      description: t(
        'CSV report showing all groups with their members, listing Group name, User Name, and Email address for each user.'
      ),
    },
  ];

  const handleCheckboxChange = (reportId: string) => {
    setSelectedReports((prev) => {
      if (prev.includes(reportId)) {
        return prev.filter((id) => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  const handleSubmit = async () => {
    if (!partner || selectedReports.length === 0 || !hasEmail) return;

    setSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const requests = selectedReports.map((reportType) => {
        return axios.post(`/api/v4/partners/${partner.id}/reports`, {
          report_type: reportType,
          include_departments: hasDepartments && includeDepartments,
        });
      });

      await Promise.all(requests);

      // Get the names of the requested reports
      const requestedReportNames = selectedReports.map((reportId) => {
        const report = reportOptions.find((r) => r.id === reportId);
        return report ? report.title : reportId;
      });

      // Create success message with requested reports
      let baseSuccessMsg = t(
        'Your report requests have been submitted successfully. You will receive each report in a separate email once it is ready. This process may take an hour or more to complete.'
      );

      // Add note about departments if they were included
      if (hasDepartments && includeDepartments) {
        baseSuccessMsg += ' ' + t('Reports will include data from all departments.');
      }

      setSuccessMessage(
        <React.Fragment>
          <div>{baseSuccessMsg}</div>
          <div style={{ marginTop: '12px' }}>
            {t('Requested reports:')}
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              {requestedReportNames.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        </React.Fragment>
      );

      setSelectedReports([]);
    } catch (error: any) {
      setErrorMessage(error?.message || t('There was an error submitting your report requests.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!partner) {
    return <Box p={4}>No partner information available.</Box>;
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h5" gutterBottom>
        {t('Emailed Reports')}
      </Typography>

      <Typography variant="body1" paragraph>
        {t(
          'Select which reports you would like to receive via email. These reports will be generated and sent to your email address. Each report will be delivered in a separate email and may take an hour or more to generate.'
        )}
      </Typography>

      {hasEmail ? (
        <Typography variant="body1" paragraph>
          <strong>
            {t('Reports will be sent to:')} {user.email}
          </strong>
        </Typography>
      ) : (
        <Box className={classes.errorMessage} mb={3}>
          <Typography variant="body1">
            <strong>{t('Warning:')}</strong>{' '}
            {t(
              'You do not have an email address on your account. Please add an email to your user profile to receive reports.'
            )}
          </Typography>
        </Box>
      )}

      {reportOptions.map((report) => (
        <Paper key={report.id} className={classes.reportCard} elevation={2}>
          <FormControlLabel
            disabled={!hasEmail}
            control={
              <Checkbox
                checked={selectedReports.includes(report.id)}
                onChange={() => handleCheckboxChange(report.id)}
                disabled={!hasEmail}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography className={classes.reportTitle} variant="h6">
                  {report.title}
                </Typography>
                <Typography className={classes.reportDescription} variant="body2">
                  {report.description}
                </Typography>
              </Box>
            }
          />
        </Paper>
      ))}

      {hasDepartments && (
        <Box mb={2} mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeDepartments}
                onChange={(e) => setIncludeDepartments(e.target.checked)}
                disabled={!hasEmail}
                color="primary"
              />
            }
            label={<Typography variant="body2">{t('Include data from departments in reports')}</Typography>}
          />
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        disabled={selectedReports.length === 0 || submitting || !hasEmail}
        onClick={handleSubmit}
        className={classes.submitButton}
      >
        {submitting ? <CircularProgress size={24} /> : t('Request Reports')}
      </Button>

      {successMessage && (
        <Box className={classes.successMessage}>
          {typeof successMessage === 'string' ? <Typography>{successMessage}</Typography> : successMessage}
        </Box>
      )}

      {errorMessage && (
        <Box className={classes.errorMessage}>
          <Typography>{errorMessage}</Typography>
        </Box>
      )}
    </Box>
  );
};
